import { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useMaintenanceApi } from '../hooks/useMaintenanceApi';
import { isHealthCheckOk } from '../healthCheckQuery';
import {
    hasMaintenanceLoginAccess,
    isMaintenanceModeActive,
} from '../maintenanceStatusQuery';
import {
    AUTH_PATH_PREFIX,
    HEALTH_CHECK_INTERVAL_MS,
    LOGIN_PATH,
    MAINTENANCE_PATH,
} from '../constants';
import {
    clearMaintenanceReturnPath,
    getMaintenanceReturnPath,
    isMaintenanceForced,
    setMaintenanceReturnPath,
} from '../redirectState';
import { useIsAuthenticated } from '@/features/identity/hooks/useIsAuthenticated';

export const MaintenanceGuard = () => {
    const location = useLocation();
    const isAuthenticated = useIsAuthenticated();

    const { healthCheckStatus, maintenanceStatus, isLoadingHealthCheckStatus } =
        useMaintenanceApi({
            refetchInterval: HEALTH_CHECK_INTERVAL_MS,
        });

    const onMaintenance = location.pathname === MAINTENANCE_PATH;
    const maintenanceForced = isMaintenanceForced();
    const isHealthy = isHealthCheckOk(healthCheckStatus);
    const maintenanceLoginAccess = hasMaintenanceLoginAccess(maintenanceStatus);
    const isAuthRoute = location.pathname.startsWith(AUTH_PATH_PREFIX);

    if (isLoadingHealthCheckStatus) return null;

    let content: ReactNode = <Outlet />;

    if (!isHealthy) {
        if (!onMaintenance) {
            setMaintenanceReturnPath(
                `${location.pathname}${location.search}${location.hash}`,
            );
            content = <Navigate to={MAINTENANCE_PATH} replace />;
        }
    } else if (onMaintenance && !maintenanceForced) {
        const returnPath = getMaintenanceReturnPath();
        clearMaintenanceReturnPath();
        content = <Navigate to={returnPath} replace />;
    } else if (isMaintenanceModeActive(maintenanceStatus)) {
        if (isAuthenticated) {
            content = <Outlet />;
        } else if (maintenanceLoginAccess) {
            if (!isAuthRoute) {
                content = <Navigate to={LOGIN_PATH} replace />;
            }
        } else if (!onMaintenance) {
            setMaintenanceReturnPath(
                `${location.pathname}${location.search}${location.hash}`,
            );
            content = <Navigate to={MAINTENANCE_PATH} replace />;
        }
    }

    return content;
};
